<template>
    <div class="main_div" :key="componentKey">
        <div style="height: auto">
            <n-space>
                <NCard style="margin-top: 20px; margin-left: 80px; border: 0px; min-width: 800px">
                    <n-space item-style="display: flex;" align="center">
                        <div style="font-size: 20px; width: 150px; font-weight: bold">기관 문의 관리</div>
                    </n-space>
                    <div class="content_delimiter_line"></div>
                    <Loading v-show="loading" />
                    <div>
                        <div v-if="listLoading" class="loading-indicator">기관 문의 목록을 불러오는 중...</div>
                        <div v-if="error" class="error-message">{{ error }}</div>
                        <div v-if="!listLoading && !error">
                            <div class="table-container">
                                <n-data-table
                                    v-if="orgMyregList.length > 0"
                                    :columns="columns"
                                    :data="orgMyregList"
                                    :pagination="false"
                                    :bordered="true"
                                />
                                <p v-else>등록된 기관 문의가 없습니다.</p>
                            </div>
                            <div
                                v-if="orgMyregList.length > 0"
                                class="pagination-controls"
                                style="margin-top: 15px; text-align: center"
                            >
                                <n-button
                                    @click="wrappedGoToPreviousPage"
                                    :disabled="currentPage === 1"
                                    style="margin-right: 8px"
                                >
                                    이전 페이지
                                </n-button>
                                <span style="margin: 0 10px">{{ currentPage }} 페이지</span>
                                <n-button @click="wrappedGoToNextPage" :disabled="!hasNextPage"> 다음 페이지 </n-button>
                            </div>
                        </div>
                    </div>
                </NCard>
                <NModal
                    v-model:show="isAlert"
                    :mask-closable="false"
                    :content="alertMessage"
                    :type="alertType"
                    preset="dialog"
                    title="Dialog"
                    positive-text="확인"
                    @positive-click="onPositiveClick"
                >
                </NModal>
                <NModal v-model:show="isShowContent">
                    <n-card
                        style="width: 600px"
                        title="내용"
                        :bordered="false"
                        size="huge"
                        role="dialog"
                        aria-modal="true"
                    >
                        {{ contentMessage }}
                    </n-card>
                </NModal>
                <NModal
                    v-model:show="isDeleteConfirm"
                    :mask-closable="false"
                    :content="deleteConfirmMessage"
                    :type="deleteConfirmType"
                    preset="dialog"
                    negative-text="취소"
                    positive-text="확인"
                    @positive-click="onDeletePositiveClick"
                    @negative-click="onDeleteNegativeClick"
                >
                </NModal>
                <NModal v-model:show="isDetailModalVisible">
                    <n-card
                        style="width: 700px; max-height: 60vh; overflow-y: auto"
                        title="기관 문의 상세 정보"
                        :bordered="false"
                        size="huge"
                        role="dialog"
                        aria-modal="true"
                    >
                        <n-descriptions
                            v-if="selectedOrgMyregDetail"
                            label-placement="left"
                            bordered
                            :column="1"
                            :label-style="{ width: '200px;' }"
                        >
                            <n-descriptions-item label="기관명">{{
                                selectedOrgMyregDetail.orgName
                            }}</n-descriptions-item>
                            <n-descriptions-item label="담당부서">{{
                                selectedOrgMyregDetail.deptName || "N/A"
                            }}</n-descriptions-item>
                            <n-descriptions-item label="담당자 이름">{{
                                selectedOrgMyregDetail.name
                            }}</n-descriptions-item>
                            <n-descriptions-item label="이메일">{{ selectedOrgMyregDetail.email }}</n-descriptions-item>
                            <n-descriptions-item label="연락처">{{
                                selectedOrgMyregDetail.phoneNumber || "N/A"
                            }}</n-descriptions-item>
                            <n-descriptions-item label="우편번호">{{
                                selectedOrgMyregDetail.address?.postCode || "N/A"
                            }}</n-descriptions-item>
                            <n-descriptions-item label="주소">{{
                                selectedOrgMyregDetail.address?.address || "N/A"
                            }}</n-descriptions-item>
                            <n-descriptions-item label="상세주소">{{
                                selectedOrgMyregDetail.address?.addressDetail || "N/A"
                            }}</n-descriptions-item>
                            <n-descriptions-item style="white-space: pre-wrap" label="문의대상">{{
                                selectedOrgMyregDetail.targetProduct || "N/A"
                            }}</n-descriptions-item>
                            <n-descriptions-item label="인원수">{{
                                selectedOrgMyregDetail.examineeCount
                            }}</n-descriptions-item>
                            <n-descriptions-item label="내용">
                                <div style="white-space: pre-wrap; max-height: 150px; overflow-y: auto">
                                    {{ selectedOrgMyregDetail.content || "N/A" }}
                                </div>
                            </n-descriptions-item>
                            <n-descriptions-item label="등록일">{{
                                common.getDate(selectedOrgMyregDetail.createdDate)
                            }}</n-descriptions-item>
                            <!-- 처리 상태 관련 필드가 OrgMyreg 모델에 있다면 여기에 추가 가능 -->
                            <!-- 예시:
                            <n-descriptions-item label="처리상태">
                                <n-tag :type="selectedOrgMyregDetail.isProcessed ? 'success' : 'warning'">
                                    {{ selectedOrgMyregDetail.isProcessed ? "처리됨" : "미처리" }}
                                </n-tag>
                            </n-descriptions-item>
                            -->
                        </n-descriptions>
                        <template #footer>
                            <n-button @click="isDetailModalVisible = false">닫기</n-button>
                        </template>
                    </n-card>
                </NModal>
            </n-space>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, h } from "vue";
import { getCallable } from "@/config/env";
import {
    NDataTable,
    NSpace,
    NCheckbox,
    NCard,
    NSelect,
    NButton,
    NIcon,
    NModal,
    NRadio,
    NRadioGroup,
    NInput,
    FormInst,
    FormRules,
    useMessage,
    useNotification,
    NDescriptions,
    NDescriptionsItem,
    NTag,
    useDialog,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import * as common from "@/assets/common/common";
import { OrgMyreg } from "@/model/myreg"; // Myreg 모델 임포트
import { QueryInfo } from "@/model/common"; // QueryInfo 임포트
import { usePagination } from "@/composables/usePagination"; // usePagination 임포트
import { useAuth } from "@/composables/useAuth";
const { loading, isLoggedIn } = useAuth();

const message = useMessage();
const notification = useNotification();
const dialog = useDialog();

const getOrgMyregsCallable = getCallable("getOrgMyregs"); // getMyregs 함수 호출

const componentKey = ref(0); // To force re-render if needed
const selectedItem = ref<OrgMyreg | null>(null); // 선택된 항목 (수정/등록용)

const deleteConfirmMessage = ref<string | null>(null);
const contentMessage = ref<string | null>(null);
const alertMessage = ref<string | null>(null);
const alertType = ref<string | null>(null);
const isDeleteConfirm = ref<boolean>(false);
const deleteConfirmType = ref<string | null>(null);
const isShowContent = ref<boolean>(false);
const isAlert = ref<boolean>(false);

const isDetailModalVisible = ref<boolean>(false);
const selectedOrgMyregDetail = ref<OrgMyreg | null>(null);

const {
    paginatedItems: orgMyregList,
    listLoading,
    error,
    currentPage,
    hasNextPage,
    goToNextPage: composableGoToNextPage,
    goToPreviousPage: composableGoToPreviousPage,
    refreshData: composableRefreshOrgMyregs,
} = usePagination<OrgMyreg>(
    async (params) => getOrgMyregsCallable(params),
    25 // 페이지 크기
);

async function wrappedRefreshOrgMyregs() {
    loading.value = true; // 전역 로딩 시작
    await composableRefreshOrgMyregs();
    loading.value = false; // 전역 로딩 종료
}

const columns = [
    {
        title: "index",
        resizable: true,
        render(_row: OrgMyreg, rowIndex: number) {
            return rowIndex + 1;
        },
    },
    {
        title: "기관명",
        key: "orgName",
        resizable: true,
    },
    {
        title: "담당부서",
        key: "deptName",
        resizable: true,
    },
    {
        title: "담당자 이름",
        key: "name",
        resizable: true,
    },
    {
        title: "연락처",
        key: "phoneNumber",
        resizable: true,
    },
    {
        title: "인원수",
        key: "examineeCount",
        resizable: true,
    },
    {
        title: "이메일",
        key: "email",
        resizable: true,
    },
    {
        title: "등록일",
        key: "createdDate",
        render(row: OrgMyreg) {
            return common.getDate(row.createdDate);
        },
    },
    {
        title: "상세보기",
        key: "actions",
        render(row: OrgMyreg) {
            return h(
                NButton,
                {
                    size: "small",
                    onClick: () => openDetailModal(row),
                },
                { default: () => "상세보기" }
            );
        },
    },
];

function onShowContent(row: OrgMyreg) {
    isShowContent.value = true;
    contentMessage.value = row.content;
}
function onPositiveClick() {
    isAlert.value = false;
}

function openDetailModal(item: OrgMyreg) {
    selectedOrgMyregDetail.value = item;
    isDetailModalVisible.value = true;
}

async function wrappedGoToNextPage() {
    if (hasNextPage.value) {
        await composableGoToNextPage();
    }
}

async function wrappedGoToPreviousPage() {
    if (currentPage.value > 1) {
        await composableGoToPreviousPage();
    }
}

onMounted(() => {
    wrappedRefreshOrgMyregs(); // onMounted 시 데이터 로드
});
</script>

<style scoped></style>
